import { ThemeProvider } from '@teable/next-themes';
import { Toaster as SoonerToaster } from '@teable/ui-lib/shadcn/ui/sonner';
import { Toaster } from '@teable/ui-lib/shadcn/ui/toaster';
import { useRouter } from 'next/router';
import { useState, type FC, type PropsWithChildren } from 'react';
import type { IServerEnv } from './lib/server-env';
import { EnvContext } from './lib/server-env';

type Props = PropsWithChildren;

export const AppProviders: FC<Props & { env: IServerEnv }> = (props) => {
  const { children, env } = props;
  const { query } = useRouter();

  // Krossings: the embedded Content Hub follows the NOS-controlled theme. NOS is the sole theme control
  // (Ismail's directive), so we FORCE the theme it hands us. It arrives two ways: a `.krossings.com` cookie
  // `ch_theme` (light|dark) - preferred, because it survives our SSO -> /auth/login -> /base redirect chain -
  // and a `?theme=` query param as a backup (which the redirect chain can drop). Read the cookie first,
  // fall back to the query param. Read once per load: NOS re-sets the cookie and RELOADS this iframe on every
  // toggle, so a fresh read each load is enough (no live watcher needed).
  const [forcedTheme] = useState<string | undefined>(() => {
    const queryTheme = typeof query.theme === 'string' ? query.theme : undefined;
    if (typeof document === 'undefined') return queryTheme;
    const match = document.cookie.match(/(?:^|;\s*)ch_theme=(light|dark)\b/);
    return match?.[1] ?? queryTheme;
  });

  return (
    <ThemeProvider
      attribute="class"
      themeColor={{
        light: '#ffffff',
        dark: '#09090b',
      }}
      forcedTheme={forcedTheme}
    >
      <EnvContext.Provider value={env}>
        {children}
        <Toaster />
        <SoonerToaster />
      </EnvContext.Provider>
    </ThemeProvider>
  );
};
